import type { FC } from 'react'
import { Trans, useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { Blaze, Button, Paper, useSnackbar } from '@dhl-ui/components'
import { MobilePhoneIllustration } from '@dhl-ui/illustrations'
import { NativeModal } from '@dhl/native-modal'
import { useDeleteProfileMobilePhone, usePutProfileMobilePhoneValidate } from '@dhl/receiver-profile-api'

import { useGetProfile } from '~/profile/model/profile/hooks.js'
import { links } from '~/links.js'
import { useInteraction } from '../InteractionProvider.js'

export const ValidateMobileNumberInteraction: FC = () => {
  const { isOpen, onDismiss } = useInteraction()
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { data: profile } = useGetProfile()
  const { mutateAsync: validateMobilePhone } = usePutProfileMobilePhoneValidate()
  const { mutateAsync: deleteMobilePhone } = useDeleteProfileMobilePhone()
  const { enqueueSnackbar } = useSnackbar()

  const handleConfirm = async () => {
    await validateMobilePhone()
    enqueueSnackbar(t('portal.interaction.validateProfilePhoneNumber.confirmSnackbar'), { severity: 'success' })
    onDismiss()
  }

  const handleChange = async () => {
    await deleteMobilePhone()
    onDismiss()
    navigate(links.mobileNumberLink())
  }

  return (
    <NativeModal isFullscreen={false} open={isOpen}>
      <NativeModal.Content>
        <Paper sx={{ p: 3, pb: 1 }}>
          <Blaze
            description={
              <Trans
                i18nKey="portal.interaction.validateProfilePhoneNumber.description"
                values={{ mobileNumber: profile?.mobilePhone?.number }}
                components={{ strong: <strong /> }}
              />
            }
            slotProps={{ illustration: { variant: MobilePhoneIllustration } }}
            title={t('portal.interaction.validateProfilePhoneNumber.title')}
            sx={{ m: '0 auto' }}
          />
        </Paper>
      </NativeModal.Content>
      <NativeModal.Footer>
        <Button fullWidth data-testid="validate-profile-confirm" variant="contained" onClick={handleConfirm}>
          {t('portal.interaction.validateProfilePhoneNumber.confirmButton')}
        </Button>
        <Button fullWidth color="dark" data-testid="validate-profile-change" variant="text" onClick={handleChange}>
          {t('portal.interaction.validateProfilePhoneNumber.changeButton')}
        </Button>
      </NativeModal.Footer>
    </NativeModal>
  )
}
